<template>
  <v-container>
    <v-card>
      <acs-header :acs="acs" />

      <v-container>
        <v-form ref="form" @submit.prevent="store">
          <p v-if="acs.internal_name">
            {{ t("access_code_set.edit.fld_internal_name") }}:
            <code>{{ acs.internal_name }}</code>
          </p>
          <v-select
            v-model="acs.type"
            :label="t('access_code_set.edit.fld_type')"
            :items="availableTypes"
            :rules="[required]"
            required
            variant="outlined"
          />
          <v-text-field
            v-model="acs.name"
            :label="t('access_code_set.edit.fld_name')"
            name="name"
            :rules="[required, minLength(10)]"
            required
            variant="outlined"
          />
          <v-text-field
            v-model="acs.hint_title"
            :label="t('access_code_set.edit.fld_hint_title')"
            name="title"
            :rules="[required]"
            required
            variant="outlined"
          />
          <v-textarea
            v-model="acs.hint_text"
            :label="t('access_code_set.edit.fld_hint_text')"
            name="hint_text"
            :rules="[required]"
            required
            variant="outlined"
          />

          <v-row>
            <v-col cols="3">
              <v-switch
                v-model="acs.is_promo_code"
                :label="t('access_code_set.edit.fld_is_promo_code')"
              ></v-switch>
            </v-col>
            <v-col cols="3">
              <v-switch
                v-model="acs.is_bonuscard"
                :label="t('access_code_set.edit.fld_is_bonuscard')"
              ></v-switch>
            </v-col>
          </v-row>

          <v-row justify="space-between">
            <v-col cols="2">
              <v-btn class="mx-2" :to="{ name: 'access-code-set' }">{{
                t("generic.btn_close")
              }}</v-btn>
            </v-col>

            <v-col cols="2" class="text-right">
              <v-btn
                class="mx-2"
                type="submit"
                color="success"
                >{{ t("generic.btn_save") }}</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
      </v-container>

      <v-container v-if="acs.id">
        <v-card>
          <v-card-title>
            <h3 class="text-h5">
              {{ t("access_code_set.code.title") }}
            </h3>
          </v-card-title>
          <v-card-text>
            <h4>{{ t("access_code_set.code.heading__" + acs.type) }}</h4>
            <div>
              {{
                t("access_code_set.code.provided", {
                  count: codeStats.provided,
                })
              }}
            </div>
            <div>
              {{ t("access_code_set.code.used", { count: codeStats.used }) }}
            </div>
            <div v-if="codeStats.list_codes" class="pt-2">
              <p>{{ t("access_code_set.code.code_list") }}</p>
              <ul>
                <li v-for="(code, i) in codeStats.codes" :key="i">
                  {{ code }}
                </li>
              </ul>
            </div>
            <v-alert v-if="codeStats.provided === 0" type="warning">
              {{ t("access_code_set.code.non_available") }}
            </v-alert>
            <v-textarea
              class="pt-3"
              v-model="pasteCodes"
              :label="t('access_code_set.code.paste_codes')"
              variant="outlined"
              rows="5"
            />
            <v-btn
              color="primary"
              @click="addCodesViaPaste"
              :disabled="!pasteCodes"
            >
              <template v-slot:prepend>
                <v-icon>mdi-plus</v-icon>
              </template>
              {{ t("generic.btn_save") }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-container>

      <v-container v-if="acs.id">
        <v-card>
          <v-card-title>
            <h3 class="text-h5">
              {{ t("access_code_set.link_campaign.title") }}
            </h3>
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="addUuid"
              :label="t('access_code_set.link_campaign.fld_add_uuid')"
              variant="outlined"
              @keyup.enter="addCampaignViaUuid"
            />
            <div v-if="linked.length">
              <h4>{{ t("access_code_set.link_campaign.linked_heading") }}</h4>
              <ul>
                <li v-for="(c, i) in linked" :key="i">
                  {{ c.title }} ({{ t("campaign.type." + c.type) }} )
                  <code>{{ c.ident }}</code>
                </li>
              </ul>
            </div>
            <v-alert v-else type="warning">
              {{ t("access_code_set.link_campaign.non_linked") }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-container>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  apiGetAccessCodeSet,
  apiCreateAccessCodeSet,
  apiUpdateAccessCodeSet,
  apiGetAccessCodeSetLinkedCampaigns,
  apiAccessCodeSetLinkCampaigns,
  apiAccessCodeSetAddCodes,
  apiAccessCodeSetCodeStats,
} from '@/api/access-code-set.js'
import AcsHeader from './snippet/Header.vue'

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
})

const { t } = useI18n()
const router = useRouter()
const instance = getCurrentInstance()

const form = ref(null)
const acs = reactive({})
const availableTypes = ref([
  { title: t('access_code_set.edit.type__reusable'), value: 'reusable' },
  { title: t('access_code_set.edit.type__unique'), value: 'unique' },
])
const pasteCodes = ref('')
const addUuid = ref('')
const codeStats = reactive({
  provided: 0,
  used: 0,
  list_codes: false,
  codes: [],
})
const linked = ref([])

const isCreate = computed(() => {
  return props.id == 'create'
})

// Validation rules
const required = (value) => {
  return !!value || t('validation.required')
}

const minLength = (min) => (value) => {
  return (value && value.length >= min) || t('validation.min_length', { min })
}

watch(() => props.id, () => {
  fetchData()
})

onMounted(() => {
  fetchData()
})

const fetchData = async () => {
  if (isCreate.value) {
    Object.assign(acs, {})
  } else {
    const acsData = await apiGetAccessCodeSet({ id: props.id })
    if (acsData) {
      Object.assign(acs, acsData)
    }
    const linkedData = await apiGetAccessCodeSetLinkedCampaigns({
      id: props.id,
    })
    if (linkedData) {
      linked.value = linkedData
    }
    const codes = await apiAccessCodeSetCodeStats({
      id: props.id,
    })
    if (codes) {
      Object.assign(codeStats, codes)
    }
  }
}

const store = async () => {
  const { valid } = await form.value.validate()
  if (!valid) return

  let acsData
  if (isCreate.value) {
    acsData = await apiCreateAccessCodeSet(acs)
  } else {
    acsData = await apiUpdateAccessCodeSet({ id: props.id }, acs)
  }
  if (acsData) {
    Object.assign(acs, acsData)
    if (isCreate.value) {
      router.push({
        name: 'access-code-set-edit',
        params: { id: acsData.id },
      })
    }
    instance.appContext.config.globalProperties.$alert({
      message: t(
        'access_code_set.edit.' +
          (isCreate.value ? 'msg_created' : 'msg_updated')
      ),
      type: 'success',
    })
  }
}

const addCampaignViaUuid = async () => {
  const linkedData = await apiAccessCodeSetLinkCampaigns(
    {
      id: props.id,
    },
    [{ ident: addUuid.value }]
  )
  if (linkedData) {
    linked.value = linkedData
  }
  addUuid.value = ''
}

const addCodesViaPaste = async () => {
  const codes = await apiAccessCodeSetAddCodes(
    {
      id: props.id,
    },
    { codes: pasteCodes.value }
  )
  if (codes) {
    Object.assign(codeStats, codes)
  }
  pasteCodes.value = ''
}
</script>
